import { listQualifications, listSubjectAliases, listSubjects } from '@/services/api/qualification'
import { listTokenShop } from '@/services/api/tokenShop'
import { timetableDataQualityApi, type CodeConcept } from '@/services/api/timetableDataQuality'
import { loadIgnoredTokenNames } from '@/utils/ignoredTokens'
import {
  EMPTY_SPLIT_HINTS,
  type ConfirmedToken,
  type SplitConcept,
  type SplitHints,
  type SubjectRef,
} from '@/utils/splitSuggestion'

function conceptsOf(rows: CodeConcept[], kind: 'class' | 'room'): SplitConcept[] {
  return rows.map((row) => ({
    kind,
    example: row.example_text,
    pieces: (row.pieces || []).map((piece) => ({ text: piece.text, label: piece.label })),
  }))
}

async function attempt<T>(label: string, run: () => Promise<T>, fallback: T, failed: string[]): Promise<T> {
  try {
    return await run()
  } catch {
    failed.push(label)
    return fallback
  }
}

const CONFIRMED_FIELDS = new Set(['subject', 'class_name', 'klass', 'class', 'room', 'ignore'])

function addLabel(map: Map<string, string[]>, id: string, label?: string | null) {
  const text = (label || '').trim()
  if (!id || !text) return
  const list = map.get(id) || []
  if (!list.some((item) => item.toLowerCase() === text.toLowerCase())) list.push(text)
  map.set(id, list)
}

async function loadMatrixSubjects(failed: string[]): Promise<{ subjects: string[]; subjectRefs: SubjectRef[] }> {
  const [quals, headers, aliases] = await Promise.all([
    listQualifications().then((rows) => ({ ok: true as const, rows })).catch(() => ({ ok: false as const, rows: [] })),
    listSubjects(false, true).then((rows) => ({ ok: true as const, rows })).catch(() => ({ ok: false as const, rows: [] })),
    listSubjectAliases().catch(() => []),
  ])
  if (!quals.ok && !headers.ok) failed.push('Fächer')
  const labels = new Map<string, string[]>()
  const matrixIds = new Set<string>()
  for (const row of quals.rows) {
    if (!row.subject_id) continue
    matrixIds.add(row.subject_id)
    addLabel(labels, row.subject_id, row.subject_name)
    addLabel(labels, row.subject_id, row.subject_code)
  }
  for (const row of headers.rows) {
    matrixIds.add(row.id)
    addLabel(labels, row.id, row.name)
    addLabel(labels, row.id, row.code)
  }
  for (const alias of aliases) {
    if (alias.is_confirmed === false) continue
    if (!matrixIds.has(alias.canonical_subject_id)) continue
    addLabel(labels, alias.canonical_subject_id, alias.alias_name)
  }
  const subjectRefs = [...labels].map(([id, names]) => ({ id, labels: names }))
  return { subjects: subjectRefs.flatMap((ref) => ref.labels), subjectRefs }
}

async function loadConfirmed(failed: string[]): Promise<ConfirmedToken[]> {
  const [shop, ignored] = await Promise.all([
    listTokenShop().then((tokens) => ({ ok: true as const, tokens })).catch(() => ({ ok: false as const, tokens: {} })),
    loadIgnoredTokenNames().then((names) => ({ ok: true as const, names })).catch(() => ({ ok: false as const, names: [] as string[] })),
  ])
  if (!shop.ok) failed.push('Tokens')
  if (!ignored.ok) failed.push('Ignorieren')
  const confirmed: ConfirmedToken[] = []
  for (const [text, row] of Object.entries(shop.tokens)) {
    if (!CONFIRMED_FIELDS.has(row.field)) continue
    confirmed.push({ text, field: row.field })
  }
  for (const name of ignored.names) confirmed.push({ text: name, field: 'ignore' })
  return confirmed
}

export async function loadSplitHints(): Promise<SplitHints> {
  const failed: string[] = []
  const [classRows, roomRows, matrix, confirmed] = await Promise.all([
    attempt('Klassenmuster', () => timetableDataQualityApi.listConcepts('class'), { concepts: [] as CodeConcept[] }, failed),
    attempt('Raummuster', () => timetableDataQualityApi.listConcepts('room'), { concepts: [] as CodeConcept[] }, failed),
    loadMatrixSubjects(failed),
    loadConfirmed(failed),
  ])
  return {
    ...EMPTY_SPLIT_HINTS,
    classConcepts: conceptsOf(classRows.concepts || [], 'class'),
    roomConcepts: conceptsOf(roomRows.concepts || [], 'room'),
    subjects: matrix.subjects,
    subjectRefs: matrix.subjectRefs,
    confirmed,
    failed: [...new Set(failed)],
  }
}
